import { useState, useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { unionCapabilities, getTraceCapabilities } from '../capabilities';
import { CURATED_FONTS } from '@/theme/fonts.manifest';

interface ThemeControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

export function ThemeControls({ plotlyJson, onUpdate }: ThemeControlsProps) {
  const [continuousScale, setContinuousScale] = useState<string>(((plotlyJson?.layout as any)?.meta?.lumi?.theme?.sequential?.scale) || 'Viridis');
  const [paletteOpacity, setPaletteOpacity] = useState<number>(
    typeof (plotlyJson?.layout as any)?._paletteOpacity === 'number'
      ? (plotlyJson!.layout as any)._paletteOpacity
      : 0.8
  );
  const [categoricalReversed, setCategoricalReversed] = useState<boolean>(() => {
    try {
      const theme:any = ((((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme) || {};
      return !!theme?.categoricalReversed;
    } catch { return false; }
  });
  const initialContReversed = useMemo(() => {
    try {
      const theme:any = ((((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme) || {};
      return !!(theme?.sequential?.reversed || theme?.diverging?.reversed);
    } catch { return false; }
  }, [plotlyJson?.layout?.meta]);
  const [continuousReversed, setContinuousReversed] = useState<boolean>(initialContReversed);

  const fontFamilies = CURATED_FONTS.map(f => ({ value: f.value, label: f.label }));

  const selectedFamily = useMemo(() => {
    const fam = (plotlyJson?.layout as any)?.font?.family as string | undefined;
    if (!fam || typeof fam !== 'string') return CURATED_FONTS[0].value;
    const normalized = fam.trim();
    const exists = fontFamilies.some(f => f.value === normalized);
    return exists ? normalized : CURATED_FONTS[0].value;
  }, [plotlyJson?.layout?.font?.family]);

  const themePalettes: { group: 'Qualitative' | 'Sequential' | 'Diverging'; rows: string[][] }[] = [
    {
      group: 'Qualitative',
      rows: [
        ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33', '#a65628', '#f781bf'],
        ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e', '#e6ab02', '#a6761d', '#666666'],
        ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00']
      ]
    },
    {
      group: 'Sequential',
      rows: [
        ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#084594'],
        ['#f7fcf5', '#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#005a32'],
        ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#99000d']
      ]
    },
    {
      group: 'Diverging',
      rows: [
        ['#67001f', '#b2182b', '#d6604d', '#f4a582', '#92c5de', '#4393c3', '#2166ac', '#053061'],
        ['#8e0152', '#c51b7d', '#de77ae', '#f1b6da', '#b8e186', '#7fbc41', '#4d9221', '#276419'],
        ['#543005', '#8c510a', '#bf812d', '#dfc27d', '#c7eae5', '#80cdc1', '#35978f', '#01665e']
      ]
    }
  ];

  const capsUnion = useMemo(() => {
    try {
      const data:any[] = Array.isArray(plotlyJson?.data) ? (plotlyJson?.data as any[]) : [];
      return unionCapabilities(data);
    } catch { return { hasCategorical: true, hasContinuous: false, hasColorbar: false, hasSliceTypes: false }; }
  }, [plotlyJson?.data]);
  
  const supportsContinuous = capsUnion.hasContinuous;
  const supportsCategorical = capsUnion.hasCategorical;

  const isPieOnlyChart = useMemo(() => {
    try {
      const data:any[] = Array.isArray(plotlyJson?.data) ? (plotlyJson?.data as any[]) : [];
      if (data.length === 0) return false;
      return data.every((t:any) => {
        const type = String((t?.type || '').toLowerCase());
        return type === 'pie' || type === 'funnelarea';
      });
    } catch { return false; }
  }, [plotlyJson?.data]);

  const isIndicatorOnlyChart = useMemo(() => {
    try {
      const data:any[] = Array.isArray(plotlyJson?.data) ? (plotlyJson?.data as any[]) : [];
      if (data.length === 0) return false;
      return data.every((t:any) => String((t?.type || '').toLowerCase()) === 'indicator');
    } catch { return false; }
  }, [plotlyJson?.data]);

  const shouldHideThemeControls = isPieOnlyChart || isIndicatorOnlyChart;

  const continuousOptions = ['Viridis','Plasma','Cividis','Turbo','Inferno','Magma','Blues','Greens','Greys','OrRd','PuBuGn'] as const;
  const colorscaleStops: Record<string, string[]> = useMemo(() => ({
    Viridis: ['#440154','#3b528b','#21918c','#5ec962','#fde725'],
    Plasma: ['#0d0887','#6a00a8','#b12a90','#e16462','#fca636'],
    Cividis: ['#00204c','#414766','#7f7f6e','#b9b97a','#ffffe0'],
    Turbo: ['#30123b','#3a7cbf','#29af7f','#95d840','#fde725'],
    Inferno: ['#000004','#420a68','#932567','#dd513a','#fca50a'],
    Magma: ['#000004','#3b0f70','#8c2981','#de4968','#fdea45'],
    Blues: ['#f7fbff','#c6dbef','#6baed6','#3182bd','#08519c'],
    Greens: ['#f7fcf5','#c7e9c0','#74c476','#31a354','#006d2c'],
    Greys: ['#ffffff','#d9d9d9','#969696','#636363','#252525'],
    OrRd: ['#fff7ec','#fdd49e','#fdbb84','#ef6548','#b30000'],
    PuBuGn: ['#fff7fb','#ccebc5','#a8ddb5','#43a2ca','#016c59']
  }), []);

  const getGradientCss = (name: string) => {
    const stops = colorscaleStops[name] || colorscaleStops['Viridis'];
    return `linear-gradient(to right, ${stops.join(',')})`;
  };

  const categoricalRows: string[][] = useMemo(() => themePalettes.flatMap(g => g.rows), [themePalettes]);
  const getCategoricalCss = (row: string[]) => {
    const n = Math.max(1, row.length);
    const parts: string[] = [];
    for (let i = 0; i < n; i++) {
      const start = (i / n) * 100;
      const end = ((i + 1) / n) * 100;
      parts.push(`${row[i]} ${start}%`, `${row[i]} ${end}%`);
    }
    return `linear-gradient(to right, ${parts.join(',')})`;
  };

  const selectedCategoricalIndex = useMemo(() => {
    try {
      const sel: string[] | undefined = ((plotlyJson?.layout as any)?.meta as any)?.lumi?.theme?.categorical;
      if (Array.isArray(sel) && sel.length) {
        const key = sel.join('|');
        let idx = categoricalRows.findIndex(r => r.join('|') === key);
        if (idx >= 0) return idx;
        const idxRev = categoricalRows.findIndex(r => r.slice().reverse().join('|') === key);
        if (idxRev >= 0) return idxRev;
        return -1;
      }
      return -1;
    } catch { return -1; }
  }, [plotlyJson?.layout, categoricalRows]);

  const categoricalPreviewRow = useMemo(() => {
    try {
      const cw:any = (plotlyJson?.layout as any)?.colorway;
      if (Array.isArray(cw) && cw.length >= 2 && cw.every((c:any) => typeof c === 'string')) return cw as string[];
      const sel: string[] | undefined = ((plotlyJson?.layout as any)?.meta as any)?.lumi?.theme?.categorical;
      if (Array.isArray(sel) && sel.length) return sel;
      const idx = selectedCategoricalIndex;
      if (idx >= 0) return categoricalRows[idx];
      const theme:any = ((((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme) || {};
      const swatches: string[] | undefined = Array.isArray(theme?.swatches) ? theme.swatches : undefined;
      if (Array.isArray(swatches) && swatches.length >= 2) return swatches;
      return categoricalRows[0];
    } catch { return categoricalRows[0]; }
  }, [plotlyJson?.layout, categoricalRows, selectedCategoricalIndex]);

  // Removed automatic palette opacity initialization - opacity should only be set explicitly by user

  useEffect(() => {
    try {
      const theme:any = ((((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme) || {};
      const next:string = (theme?.sequential?.scale as string) || (theme?.continuous as string) || 'Viridis';
      if (typeof next === 'string' && next && next !== continuousScale) {
        setContinuousScale(next);
      }
    } catch {}
  }, [plotlyJson?.layout?.meta, continuousScale]);

  useEffect(() => {
    try {
      const theme:any = ((((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme) || {};
      if (typeof theme?.categoricalReversed === 'boolean') {
        if (theme.categoricalReversed !== categoricalReversed) setCategoricalReversed(!!theme.categoricalReversed);
        return;
      }
      const sel: string[] | undefined = theme?.categorical;
      if (Array.isArray(sel) && sel.length) {
        const key = sel.join('|');
        const idxRev = categoricalRows.findIndex(r => r.slice().reverse().join('|') === key);
        if (idxRev >= 0 && !categoricalReversed) setCategoricalReversed(true);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotlyJson?.layout?.meta, categoricalRows]);

  const showMissingCategory = useMemo(() => {
    try {
      const theme:any = (((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme || {};
      return !!(theme?.mappings?.by);
    } catch { return false; }
  }, [plotlyJson?.layout]);

  return (
    <div className="space-y-3">
      {/* Show message when theme controls are hidden for pie/indicator charts */}
      {shouldHideThemeControls && (
        <div className="text-xs text-muted-foreground p-2 bg-muted rounded">
          {isPieOnlyChart 
            ? "Theme color controls are not available for pie/doughnut charts. Use the Series tab to edit individual slice colors."
            : "Theme color controls are not available for bullet charts. Use the Series tab to edit bar, band, and target line colors."}
        </div>
      )}

      {/* Theme Palettes (categorical) */}
      {supportsCategorical && !shouldHideThemeControls && !(Array.isArray(plotlyJson?.data) && (plotlyJson!.data as any[]).length > 0 && (plotlyJson!.data as any[]).every((t:any) => String((t?.type||'').toLowerCase()) === 'waterfall')) && (
        <div>
          <Label className="text-sm text-muted-foreground mb-2 block">Categorical palette</Label>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Reversed</Label>
              <Switch
                checked={categoricalReversed}
                onCheckedChange={(checked) => {
                  setCategoricalReversed(checked);
                  try {
                    const currentPalette = categoricalPreviewRow.slice();
                    const reversedPalette = currentPalette.slice().reverse();
                    onUpdate('layout.meta.lumi.theme.categoricalReversed', checked);
                    onUpdate('layout.meta.lumi.theme.categorical', reversedPalette);
                    onUpdate('design.colorPalette', reversedPalette);
                  } catch {}
                }}
              />
            </div>
            <Select value={selectedCategoricalIndex >= 0 ? String(selectedCategoricalIndex) : undefined} onValueChange={(v) => {
              const idx = parseInt(v);
              const row = categoricalRows[idx];
              const rowToApply = categoricalReversed ? row.slice().reverse() : row;
              onUpdate('layout.meta.lumi.theme.categorical', rowToApply);
              onUpdate('layout.meta.lumi.theme.categoricalReversed', categoricalReversed);
              onUpdate('design.colorPalette', rowToApply);
            }}>
              <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
                <div className="w-full h-3 rounded" style={{ background: getCategoricalCss(categoricalPreviewRow) as any }} />
              </SelectTrigger>
              <SelectContent align="center" position="popper" className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] p-1">
                {categoricalRows.map((row, idx) => (
                  <SelectItem
                    key={idx}
                    value={String(idx)}
                    className="p-2"
                    onClick={() => {
                      const rowToApply = categoricalReversed ? row.slice().reverse() : row;
                      onUpdate('layout.meta.lumi.theme.categorical', rowToApply);
                      onUpdate('layout.meta.lumi.theme.categoricalReversed', categoricalReversed);
                      onUpdate('design.colorPalette', rowToApply);
                    }}
                  >
                    <div className="h-3 w-full rounded" style={{ background: getCategoricalCss(row) as any }} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Opacity for all palette-driven fills */}
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-muted-foreground">Palette opacity</Label>
                <span className="text-[11px] text-muted-foreground">{Math.round(paletteOpacity * 100)}%</span>
              </div>
              <Slider
                value={[paletteOpacity]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([v]) => {
                  setPaletteOpacity(v);
                  onUpdate('design.palette.opacity', v);
                }}
              />
            </div>
          </div>
        </div>
      )}
      {!supportsCategorical && capsUnion.hasContinuous && (
        <div className="text-xs text-muted-foreground">Heatmaps use a continuous palette</div>
      )}

      {/* Continuous colorscale */}
      {supportsContinuous && !shouldHideThemeControls && !(Array.isArray(plotlyJson?.data) && (plotlyJson!.data as any[]).length > 0 && (plotlyJson!.data as any[]).every((t:any) => String((t?.type||'').toLowerCase()) === 'waterfall')) && (
        <div className="mt-4">
          <Label className="text-sm text-muted-foreground mb-2 block">Continuous colorscale</Label>
          <Select onValueChange={(v) => { setContinuousScale(v); onUpdate('op.continuousScale:set', { scale: v }); }} value={continuousScale}>
            <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
              <div className="w-full h-3 rounded" style={{ background: getGradientCss(continuousScale) as any }} />
            </SelectTrigger>
            <SelectContent align="center" position="popper" className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] p-1">
              {continuousOptions.map((name) => (
                <SelectItem
                  key={name}
                  value={name}
                  className="p-2"
                  onClick={() => {
                    setContinuousScale(name);
                    onUpdate('op.continuousScale:set', { scale: name });
                  }}
                >
                  <div className="h-3 w-full rounded" style={{ background: getGradientCss(name) as any }} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 flex items-center justify-between">
            <Label className="text-sm">Reversed</Label>
            <Switch
              checked={continuousReversed}
              onCheckedChange={(checked) => {
                setContinuousReversed(checked);
                onUpdate('layout.meta.lumi.theme.sequential.reversed', checked);
                onUpdate('layout.meta.lumi.theme.diverging.reversed', checked);
              }}
            />
          </div>
        </div>
      )}

      {/* Reset explicit colors to theme */}
      <div className="mt-4">
        <Label className="text-sm text-muted-foreground mb-1 block">Theme actions</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => onUpdate('design.theme.reset', true)}
              >
                Reset colors to Theme
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Clear per‑trace colors and reapply the current Theme defaults.</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {showMissingCategory && (
          <div className="mt-3">
            <Label className="text-sm text-muted-foreground">Missing category color</Label>
            <ColorPicker value={(((plotlyJson?.layout as any)?.meta as any)?.lumi as any)?.theme?.mappings?.missing || '#9e9e9e'} onChange={(c) => onUpdate('design.theme.missingCategoryColor', c)} />
          </div>
        )}
        <div className="mt-2 text-xs">
          <span id="lumi-new-cats-chip" className="text-muted-foreground"></span>
        </div>
      </div>

      {/* Typography */}
      <div className="space-y-0 pt-2 border-t">
        <Label className="text-sm text-muted-foreground">Typography</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="text-sm text-muted-foreground">Font family</Label>
            <Select value={selectedFamily} onValueChange={(value) => onUpdate('design.font.family', value)}>
              <SelectTrigger id="font-family" className="mt-1 h-10" title="Curated for consistency & performance; fonts shown are guaranteed to load.">
                <SelectValue placeholder="Select font" />
              </SelectTrigger>
              <SelectContent>
                {fontFamilies.map(font => (
                  <SelectItem key={font.value} value={font.value}>
                    {font.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground">Font size</Label>
            <Input
              id="font-size"
              type="number"
              min={6}
              max={72}
              step={1}
              placeholder="Font size"
              className="mt-1"
              defaultValue={plotlyJson?.layout?.font?.size || 12}
              onChange={(e) => onUpdate('design.font.size', parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground">Font color</Label>
            <ColorPicker
              value={plotlyJson?.layout?.font?.color || '#000000'}
              onChange={(color) => onUpdate('design.font.color', color)}
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground">Font weight</Label>
            <Select defaultValue={String((plotlyJson?.layout as any)?.font?.weight || 400)} onValueChange={(v) => onUpdate('design.font.weight', parseInt(v))}>
              <SelectTrigger id="font-weight" className="mt-1 h-10">
                <SelectValue placeholder="Weight" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="400">Regular</SelectItem>
                <SelectItem value="500">Medium</SelectItem>
                <SelectItem value="600">Semibold</SelectItem>
                <SelectItem value="700">Bold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Line Width (all series) - Global control */}
      {(() => {
        const traces = plotlyJson?.data || [];
        // Check if any trace supports lines
        const hasLineSupport = traces.some((trace: any) => {
          const caps = getTraceCapabilities(trace);
          return caps.supportsLine;
        });
        
        if (!hasLineSupport || traces.length === 0) return null;
        
        // Compute global line width value: check if all traces have the same line.width
        const lineWidths = traces
          .map((trace: any) => trace.line?.width ?? 2)
          .filter((w: number) => typeof w === 'number');
        const allSame = lineWidths.length > 0 && lineWidths.every((w: number) => w === lineWidths[0]);
        const firstTraceWithLine = traces.find((trace: any) => {
          const caps = getTraceCapabilities(trace);
          return caps.supportsLine;
        });
        const globalLineWidth = allSame 
          ? lineWidths[0] 
          : (firstTraceWithLine?.line?.width ?? 2);
        const hasMixedValues = !allSame && lineWidths.length > 1;
        
        return (
          <div className="space-y-0 pt-2 border-t">
            <Label className="text-sm text-muted-foreground">Line Width (all series)</Label>
            <div className="mt-1 space-y-2">
              <Slider
                value={[globalLineWidth]}
                onValueChange={([v]) => {
                  onUpdate('series.all.line.width', v);
                }}
                min={0}
                max={10}
                step={0.5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>None</span>
                <span>{globalLineWidth}px{hasMixedValues ? ' (Mixed values)' : ''}</span>
                <span>Thick</span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}


